import { Alert, AlertDescription } from "@promptwatch/ui/components/alert";
import { Button } from "@promptwatch/ui/components/button";
import { Card, CardContent } from "@promptwatch/ui/components/card";
import { Input } from "@promptwatch/ui/components/input";
import { Label } from "@promptwatch/ui/components/label";
import { ArrowRight, Copy, Download, FileText, LetterText, LoaderCircle, Search, Shield } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import BaseLayout from "@/components/base-layout";
import { Header } from "@/components/header";
import { popModal, pushModal } from "@/components/modals";
import { clientEnv } from "@/env/client";
import { trpc } from "@/utils/trpc";

const LlmsTxtGenerator = () => {
  const posthog = usePostHog();
  const router = useRouter();

  const [formData, setFormData] = useState({
    website: "",
  });

  const [generatedContent, setGeneratedContent] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated (has a free tools token)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const token = localStorage.getItem("free-tools-token");
      setIsAuthenticated(!!token);
    }
  }, []);

  const generateLLMTxt = trpc.freeTools.generateLLMTxt.useMutation({
    onSuccess: (data) => {
      if (data.success && data.llmTxt) {
        setGeneratedContent(data.llmTxt);
        setError(null);
        posthog?.capture("llms_txt_generated", {
          website: formData.website,
          pagesCrawled: data.metadata?.pagesCrawled,
        });
        toast.success("Successfully generated llms.txt!");
      }
    },
    onError: (error) => {
      console.error("Error generating llms.txt:", error);
      const errorMessage = error.message || "Failed to generate llms.txt. Please try again.";

      // Check if error is due to authentication
      if (error.message?.includes("UNAUTHORIZED") || error.data?.code === "UNAUTHORIZED") {
        pushModal("freeToolsSignup", {
          onSuccess: () => {
            // Re-check authentication status and close modal
            const token = localStorage.getItem("free-tools-token");
            setIsAuthenticated(!!token);
            popModal();
            setError(null);
            toast.success("You can now use the generator!");
          },
          toolName: "llms.txt generator",
        });
        setError("Please sign up or log in to use this tool.");
      } else {
        setError(errorMessage);
      }

      toast.error(errorMessage);
    },
  });

  const handleInputChange = (value: string) => {
    setFormData((prev) => ({ ...prev, website: value }));
    setError(null); // Clear error when user types
  };

  const handleGenerate = () => {
    if (!formData.website) {
      return;
    }

    // Check if user is authenticated before proceeding
    if (!isAuthenticated) {
      pushModal("freeToolsSignup", {
        onSuccess: () => {
          // Re-check authentication status and close modal
          const token = localStorage.getItem("free-tools-token");
          setIsAuthenticated(!!token);
          popModal();
          setError(null);
          toast.success("You can now use the generator!");
        },
        toolName: "llms.txt generator",
      });
      setError("Please sign up or log in to use this tool.");
      return;
    }

    setError(null);

    // Ensure the URL has a protocol
    let websiteUrl = formData.website;
    if (!websiteUrl.startsWith("http://") && !websiteUrl.startsWith("https://")) {
      websiteUrl = `https://${websiteUrl}`;
    }

    generateLLMTxt.mutate({
      websiteUrl,
      pagesToCrawl: 3, // Default to 3 pages
    });
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    posthog?.capture("llms_txt_copied");
  };

  const handleDownload = () => {
    const blob = new Blob([generatedContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "llms.txt";
    a.click();
    window.URL.revokeObjectURL(url);
    posthog?.capture("llms_txt_downloaded");
  };

  return (
    <BaseLayout>
      <Header
        canonical="https://promptwatch.com/free-tools/llms-txt-generator"
        title="Free LLMs.txt Generator Tool | Create AI-Optimized Website Files | Promptwatch.com"
        description="Generate an optimized LLMs.txt file for your website to improve AI search visibility. Our free tool helps ChatGPT, Claude, and other AI models better understand and recommend your business. Perfect for SaaS, e-commerce, and service providers."
      />
      <main className="overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-200/50 via-blue-100/50" />
        <div
          aria-hidden="true"
          className="absolute h-full w-full scale-[0.8] bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] [mask-image:radial-gradient(ellipse_50%_50%_at_50%_50%,#fff_70%,transparent_100%)]"
        />

        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07]">
              <nav aria-label="Breadcrumb" className="p-3">
                <ol className="flex items-center text-sm">
                  <li>
                    <Link href="/" className="text-primary hover:underline">
                      Home
                    </Link>
                  </li>
                  <li className="mx-2 text-muted-foreground">/</li>
                  <li>
                    <Link href="/free-tools" className="text-primary hover:underline">
                      Free Tools
                    </Link>
                  </li>
                  <li className="mx-2 text-muted-foreground">/</li>
                  <li className="text-gray-700" aria-current="page">
                    LLMs.txt Generator
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </section>

        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11 z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm z-40" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11 z-40" />
            <div className="relative border-x border-gray-950/[0.07] px-4 pb-16 pt-16 sm:px-12">
              <div className="relative w-fit bg-gray-950/5 p-2  mb-6">
                <div aria-hidden="true" className="absolute left-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute right-1 top-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 left-1 size-[3px] rounded-full bg-gray-950/20" />
                <div aria-hidden="true" className="absolute bottom-1 right-1 size-[3px] rounded-full bg-gray-950/20" />
                <div className="relative flex h-fit items-center gap-2 rounded-full bg-white px-3 py-1 shadow">
                  <span className="text-title text-sm flex items-center gap-2 px-1">Free AI SEO Tools</span>
                </div>
              </div>

              <h1 className="text-title mt-6 text-4xl font-medium max-w-3xl sm:mt-12 sm:text-5xl lg:text-5xl">
                Free LLMs.txt Generator: Boost Your AI Search Visibility
              </h1>

              <p className="text-lg pt-8 max-w-3xl text-muted-foreground">
                Create an optimized LLMs.txt file to help AI models like ChatGPT, Claude, and Perplexity better understand and recommend
                your business. Improve your AI search visibility and get more accurate recommendations from AI assistants.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content Section with White Background */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -left-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 left-3 size-2 border shadow-sm sm:left-5 md:left-11" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 -right-1 size-2 border shadow-sm" />
            <div aria-hidden="true" className="bg-ui absolute -bottom-1 right-3 size-2 border shadow-sm sm:right-5 md:right-11" />
            <div className="relative border-x border-gray-950/[0.07] bg-white/40 relative">
              <div className="px-5 sm:px-20 py-16">
                {/* Input Section */}
                <div className="mx-auto">
                  <Label htmlFor="website" className="text-lg font-medium mb-3 block ">
                    Enter your website URL
                  </Label>
                  <div className="flex flex-col md:flex-row gap-3">
                    <Input
                      id="website"
                      type="url"
                      placeholder="https://promptwatch.com"
                      value={formData.website}
                      onChange={(e) => handleInputChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !generateLLMTxt.isPending && formData.website) {
                          handleGenerate();
                        }
                      }}
                      className="h-14 text-lg px-4 md:flex-1 bg-white"
                      disabled={generateLLMTxt.isPending}
                    />
                    <Button
                      size="lg"
                      onClick={handleGenerate}
                      disabled={generateLLMTxt.isPending || !formData.website}
                      className="h-14 px-8 text-lg rounded-md w-full md:w-auto"
                    >
                      {generateLLMTxt.isPending ? (
                        <>
                          <LoaderCircle className="animate-spin h-4 w-4 mr-2" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <LetterText className="size-5 mr-2" />
                          Generate
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    We'll generate a comprehensive llms.txt template customized for your website
                  </p>
                </div>

                {/* Error Alert */}
                {error && (
                  <Alert variant="destructive" className="max-w-2xl mx-auto mb-8">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {/* Loading State */}
                {generateLLMTxt.isPending && (
                  <div className="mt-12 mx-auto mb-12">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <div className="space-y-3">
                        <div>
                          <p className="font-medium text-blue-900">Analyzing your website...</p>
                          <p className="text-sm text-blue-700 mt-1">
                            We're crawling your website and generating a comprehensive llms.txt file. This may take 30-60 seconds.
                          </p>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                          <div
                            className="h-2 rounded-full bg-gradient-to-r from-transparent via-blue-600 to-transparent animate-[slide_1.5s_ease-in-out_infinite]"
                            style={{
                              width: "40%",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <style jsx>{`
                      @keyframes slide {
                        0% { 
                          transform: translateX(-100%);
                        }
                        100% { 
                          transform: translateX(350%);
                        }
                      }
                    `}</style>
                  </div>
                )}

                {/* Preview Section */}
                {generatedContent && !generateLLMTxt.isPending && (
                  <div className="mx-auto">
                    <div className="flex items-center justify-between mb-4 mt-8">
                      <h2 className="text-xl font-semibold">Generated llms.txt</h2>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={handleCopy}>
                          {copied ? (
                            "Copied!"
                          ) : (
                            <>
                              <Copy className="size-4 mr-2" /> Copy
                            </>
                          )}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleDownload}>
                          <Download className="size-4 mr-2" /> Download
                        </Button>
                      </div>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 font-mono text-sm whitespace-pre-wrap overflow-x-auto">
                      {generatedContent}
                    </div>
                    <p className="text-sm text-muted-foreground mt-4">
                      Replace the placeholder text in brackets with your actual company information before using this file.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div className="relative border-x border-gray-950/[0.07] px-4 py-16 sm:px-12">
              <h2 className="text-3xl font-semibold mb-2">Why Use LLMs.txt for AI SEO?</h2>

              <p className="text-gray-700 mb-6">
                As AI assistants become the primary way users discover products and services, having an LLMs.txt file is crucial for your
                digital presence. When users ask AI assistants about products, services, or solutions in your industry, these models rely on
                their training data to provide recommendations. By implementing an LLMs.txt file, you're giving AI models a clear,
                structured understanding of what your business offers, ensuring accurate representation in AI-generated responses and
                improving your visibility in AI search results.
              </p>

              <div className="grid gap-6 md:grid-cols-3">
                <Card>
                  <CardContent className="pt-6">
                    <Search className="size-8 text-blue-600 mb-4" />
                    <h3 className="font-semibold mb-2">Better AI Discovery</h3>
                    <p className="text-sm text-muted-foreground">
                      Help AI models understand your business and recommend it in relevant contexts
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <FileText className="size-8 text-blue-600 mb-4" />
                    <h3 className="font-semibold mb-2">Structured Information</h3>
                    <p className="text-sm text-muted-foreground">
                      Provide clear, organized information that AI systems can easily parse and understand
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <Shield className="size-8 text-blue-600 mb-4" />
                    <h3 className="font-semibold mb-2">Control Your Narrative</h3>
                    <p className="text-sm text-muted-foreground">
                      Ensure AI models have accurate, up-to-date information about your business
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-12 space-y-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4">Who Needs LLMs.txt for AI Optimization?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <ArrowRight className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">SaaS Companies:</span>
                        <span className="text-sm text-muted-foreground ml-1">
                          Ensure AI accurately describes your features when users compare solutions
                        </span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">E-commerce Businesses:</span>
                        <span className="text-sm text-muted-foreground ml-1">Help AI shopping assistants recommend your products</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Service Providers:</span>
                        <span className="text-sm text-muted-foreground ml-1">Make sure AI knows your service areas and specialties</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowRight className="size-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium">Content Creators:</span>
                        <span className="text-sm text-muted-foreground ml-1">Help AI understand and cite your expertise correctly</span>
                      </div>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-4">AI SEO: The Future of Digital Visibility</h3>
                  <p className="text-gray-700 mb-4">
                    While traditional SEO optimizes for Google searches, AI SEO optimizes for ChatGPT and other AI assistant queries. As
                    more people use AI for research and recommendations, your LLMs.txt file becomes your essential AI visibility strategy.
                    Stay ahead of the curve and ensure your business is accurately represented in AI-generated responses.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>• 70% of users now use AI for product research and recommendations</li>
                    <li>• AI-powered recommendations drive 3x higher conversion rates</li>
                    <li>• Early adopters of AI SEO see 40% increase in AI-driven traffic</li>
                    <li>• 85% of businesses report improved accuracy in AI recommendations after implementing LLMs.txt</li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 border-l-4 border-blue-500 pl-6">
                <h4 className="font-semibold mb-2">How LLMs.txt Improves Your AI Visibility</h4>
                <ol className="space-y-2 text-sm text-gray-700">
                  <li>
                    <span className="font-medium">1. Create:</span> Generate your LLMs.txt file with comprehensive business information
                  </li>
                  <li>
                    <span className="font-medium">2. Deploy:</span> Place the file at yourwebsite.com/llms.txt
                  </li>
                  <li>
                    <span className="font-medium">3. Discovered:</span> AI models find and parse your structured data
                  </li>
                  <li>
                    <span className="font-medium">4. Indexed:</span> Your information is stored in AI knowledge bases
                  </li>
                  <li>
                    <span className="font-medium">5. Recommended:</span> AI suggests your business when relevant queries arise
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative border-b border-gray-950/[0.07]">
          <div className="relative mx-auto max-w-6xl border-x border-gray-950/[0.07] px-4 sm:px-6 md:px-12">
            <div className="px-5 sm:px-20 border-x border-gray-950/[0.07] hover:!bg-ui relative p-8 shadow-lg shadow-gray-900/5 before:absolute before:inset-1 before:rounded-b-lg before:bg-gradient-to-t before:from-blue-200 group-hover:bg-transparent">
              <div className="flex flex-col md:flex-row items-center justify-between">
                <div className="mb-6 md:mb-0 z-40">
                  <h3 className="text-xl font-semibold mb-2">Track Your AI Search Performance</h3>
                  <p className="text-gray-800/80 max-w-xl">
                    Get insights into how AI assistants like ChatGPT and Claude recommend your brand, and optimize your presence across all
                    major AI platforms.
                  </p>
                </div>
                <div className="flex flex-row gap-4 z-40">
                  <Button
                    variant="outline"
                    onClick={() => {
                      posthog.capture("book_demo_button_clicked", {
                        source: "llms_txt_generator",
                      });
                      router.push("/book-a-demo");
                    }}
                  >
                    Book a Demo
                  </Button>
                  <Button
                    className="flex items-center gap-2"
                    onClick={() => {
                      posthog.capture("get_started_button_clicked", {
                        source: "llms_txt_generator",
                      });
                      router.push(clientEnv.NEXT_PUBLIC_APP_URL);
                    }}
                  >
                    Get Started <ArrowRight className="size-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </BaseLayout>
  );
};

export default LlmsTxtGenerator;
